import { createContext, useContext, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, MessageCircle, Calculator, RotateCcw } from "lucide-react";
import { useLanguage, waLink } from "../i18n";

const EASE = [0.22, 1, 0.36, 1];

export const SERVICES = [
  { id: "repairs", min: 30, max: 60 },
  { id: "furniture", min: 40, max: 120 },
  { id: "tv", min: 50, max: 90 },
  { id: "lighting", min: 35, max: 70 },
  { id: "pictures", min: 30, max: 60 },
  { id: "doors", min: 40, max: 110 },
  { id: "sofa", min: 60, max: 120 },
  { id: "carpet", min: 40, max: 90 },
  { id: "car", min: 50, max: 100 },
  { id: "pack", min: null, max: null },
];

const EstimatorContext = createContext({ open: () => {} });
export const useEstimator = () => useContext(EstimatorContext);

export const EstimatorProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <EstimatorContext.Provider value={{ open: () => setIsOpen(true) }}>
      {children}
      <AnimatePresence>{isOpen && <EstimatorModal onClose={() => setIsOpen(false)} />}</AnimatePresence>
    </EstimatorContext.Provider>
  );
};

export const EstimatorButton = ({ className = "", testId = "estimator-open-button", iconSize = 18 }) => {
  const { t } = useLanguage();
  const { open } = useEstimator();
  return (
    <button type="button" data-testid={testId} onClick={open} className={className}>
      <Calculator size={iconSize} strokeWidth={2.4} />
      {t.estimate.open}
    </button>
  );
};

const buildMessage = (t, selected) => {
  const lines = selected.map((s) => {
    const range = s.min ? `${s.min}–${s.max} €` : t.estimate.waConsult;
    return `• ${t.estimate.services[s.id]} (${range})`;
  });
  const priced = selected.filter((s) => s.min);
  const min = priced.reduce((a, s) => a + s.min, 0);
  const max = priced.reduce((a, s) => a + s.max, 0);
  const total = priced.length ? `${t.estimate.waEstimate}: ${min}–${max} €` : "";
  return [t.estimate.waIntro, ...lines, total].filter(Boolean).join("\n");
};

const EstimatorModal = ({ onClose }) => {
  const { t } = useLanguage();
  const [picked, setPicked] = useState([]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const selected = SERVICES.filter((s) => picked.includes(s.id));
  const priced = selected.filter((s) => s.min);
  const hasConsult = selected.some((s) => !s.min);
  const min = priced.reduce((a, s) => a + s.min, 0);
  const max = priced.reduce((a, s) => a + s.max, 0);

  return (
    <motion.div
      data-testid="estimator-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={onClose}
      className="fixed inset-0 z-[90] flex items-end justify-center bg-[#0D1B2A]/70 backdrop-blur-sm sm:items-center sm:p-6"
    >
      <motion.div
        data-testid="estimator-modal"
        role="dialog"
        aria-modal="true"
        initial={{ opacity: 0, y: 60, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 40, scale: 0.98 }}
        transition={{ duration: 0.5, ease: EASE }}
        onClick={(e) => e.stopPropagation()}
        className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-[2rem] bg-[#FAF6F0] shadow-2xl shadow-black/40 sm:rounded-[2rem]"
      >
        <div className="flex items-start justify-between gap-4 px-6 pt-6 sm:px-10 sm:pt-9">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.25em] text-[#C85A32]">{t.estimate.eyebrow}</p>
            <h3 className="font-serif text-3xl font-black tracking-tight text-[#0D1B2A] sm:text-4xl">{t.estimate.title}</h3>
            <p className="mt-2 max-w-lg text-sm text-[#0D1B2A]/60 sm:text-base">{t.estimate.sub}</p>
          </div>
          <button
            data-testid="estimator-close-button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#0D1B2A]/15 text-[#0D1B2A] transition-colors duration-300 hover:bg-[#0D1B2A] hover:text-[#FAF6F0]"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6 sm:px-10">
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {SERVICES.map((s, i) => {
              const on = picked.includes(s.id);
              return (
                <motion.button
                  key={s.id}
                  type="button"
                  data-testid={`estimator-service-${s.id}`}
                  aria-pressed={on}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.05 + i * 0.03, ease: EASE }}
                  onClick={() => toggle(s.id)}
                  className={`group flex items-center gap-3 rounded-2xl border px-4 py-3.5 text-left transition-all duration-300 ${
                    on
                      ? "border-[#0D1B2A] bg-[#0D1B2A] text-[#FAF6F0] shadow-lg shadow-[#0D1B2A]/20"
                      : "border-[#0D1B2A]/12 bg-white text-[#0D1B2A] hover:border-[#C85A32]"
                  }`}
                >
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-300 ${
                      on ? "border-[#25D366] bg-[#25D366] text-white" : "border-[#0D1B2A]/25 group-hover:border-[#C85A32]"
                    }`}
                  >
                    {on && <Check size={13} strokeWidth={3} />}
                  </span>
                  <span className="flex-1 text-sm font-semibold leading-snug">{t.estimate.services[s.id]}</span>
                  <span className={`shrink-0 text-xs font-bold ${on ? "text-[#FAF6F0]/70" : "text-[#1B6B93]"}`}>
                    {s.min ? `${s.min}–${s.max} €` : t.estimate.consult}
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>

        <div className="border-t border-[#0D1B2A]/10 bg-white px-6 py-5 sm:px-10 sm:py-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0D1B2A]/50">{t.estimate.total}</p>
              <p data-testid="estimator-total" className="mt-1 font-serif text-3xl font-black text-[#0D1B2A]">
                {selected.length === 0 ? (
                  <span className="text-lg font-semibold text-[#0D1B2A]/40">{t.estimate.empty}</span>
                ) : priced.length === 0 ? (
                  <span>{t.estimate.consult}</span>
                ) : (
                  <>
                    {min}–{max} €{hasConsult && <span className="ml-2 text-base font-semibold text-[#C85A32]">+ {t.estimate.consult.toLowerCase()}</span>}
                  </>
                )}
              </p>
              <p className="mt-1 text-xs text-[#0D1B2A]/50">
                {selected.length > 0 && `${selected.length} ${t.estimate.selected} · `}
                {t.estimate.note}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {selected.length > 0 && (
                <button
                  data-testid="estimator-clear-button"
                  onClick={() => setPicked([])}
                  className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#0D1B2A]/15 text-[#0D1B2A]/60 transition-colors duration-300 hover:border-[#C85A32] hover:text-[#C85A32]"
                  aria-label={t.estimate.clear}
                >
                  <RotateCcw size={16} />
                </button>
              )}
              <a
                data-testid="estimator-whatsapp-cta"
                href={selected.length ? waLink(buildMessage(t, selected)) : undefined}
                target="_blank"
                rel="noopener noreferrer"
                aria-disabled={!selected.length}
                onClick={(e) => !selected.length && e.preventDefault()}
                className={`inline-flex items-center gap-2.5 whitespace-nowrap rounded-full px-6 py-3.5 text-sm font-bold text-white shadow-xl transition-all duration-300 sm:text-base ${
                  selected.length
                    ? "bg-[#25D366] shadow-[#25D366]/30 hover:scale-[1.04]"
                    : "cursor-not-allowed bg-[#0D1B2A]/20 shadow-none"
                }`}
              >
                <MessageCircle size={18} strokeWidth={2.4} />
                {t.estimate.cta}
              </a>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
